import React from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Loader2 } from 'lucide-react';

/**
 * 🛠️ cn Utility
 * Merges Tailwind classes safely, resolving conflicts (e.g., padding vs. px).
 */
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'glass';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  /**
   * Shape rule:
   * - 'rounded' (default) — block/form buttons: full-width submits, modal confirms, settings actions
   * - 'pill' — compact inline actions: "Add X", FABs, icon+label chips
   */
  shape?: 'rounded' | 'pill';
  isLoading?: boolean;
}

/**
 * 🧱 Standardized EverydayOS Button
 * A tactile, state-aware button component with standardized 
 * visual styles for all household features.
 */
export const Button: React.FC<ButtonProps> = ({
  className,
  variant = 'primary',
  size = 'md',
  shape = 'rounded',
  isLoading,
  children,
  disabled,
  ...props
}) => {
  const variants = {
    // Brand blue — primary action
    primary: 'bg-brand text-white hover:bg-brand-hover shadow-lg shadow-brand/20 active:scale-[0.97]',
    // Slate based, fits in Sidebars and Toolbars
    secondary: 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-[0.97]',
    // Rose based, used for Deletions and High-Alerts
    danger: 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-500 hover:text-white transition-all active:scale-[0.97]',
    // Bare text, fits in headers
    ghost: 'bg-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white',
    // 2px border, used for secondary actions like "Reset Layout"
    outline: 'border-2 border-slate-200 dark:border-slate-800 bg-transparent hover:border-brand dark:hover:border-brand hover:text-brand dark:hover:text-brand text-slate-700 dark:text-slate-300',
    // Frosted glass look for dashboard overlays
    glass: 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 active:scale-[0.97]',
  };

  const sizes = {
    sm: 'h-8 px-4 text-2xs font-black uppercase tracking-widest',
    md: 'h-11 px-6 text-[11px] font-black uppercase tracking-widest',
    lg: 'h-14 px-8 text-sm font-black uppercase tracking-widest',
    icon: 'h-10 w-10 p-0', // Perfect for square icon-only buttons
  };

  const shapes = {
    rounded: { sm: 'rounded-xl', md: 'rounded-2xl', lg: 'rounded-[1.5rem]', icon: 'rounded-xl' },
    pill:    { sm: 'rounded-full', md: 'rounded-full', lg: 'rounded-full', icon: 'rounded-full' },
  };

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center transition-all duration-300',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-40 disabled:grayscale',
        variants[variant],
        sizes[size],
        shapes[shape][size],
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          <span className="opacity-50 italic lowercase">syncing...</span>
        </>
      ) : (
        children
      )}
    </button>
  );
};